import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  modalVariants,
  overlayVariants,
} from "../../../../animations/Animations";
import ContentSelector from "../custom/ContentSelector";
import ProductSelector from "../custom/ProductSelector";
import { FaSpinner } from "react-icons/fa";
import { useDateOfVisit } from "../../../../contexts/report/DateOfVisitContext";
import CustomerSelector from "../custom/CustomerSelector";
import ObjectiveInput from "../custom/ObjectiveInput";

export interface CustomerSelectorProps {
  selectedCustomer: Customer | null;
  onCustomerSelect: (customer: Customer | null) => void;
  error?: string;
}

interface Customer {
  customer: string;
  time_of_visit: string;
}

interface ApiSubmissionData {
  objective: string;
  dateOfVisit: string;
  content: number[];
  products: number[];
  followupPlan: string;
  customer: string;
  timeOfVisit?: string;
  summary?: string;
}

interface FormData {
  objective: string | null;
  dateOfVisit: string;
  content: number[];
  products: number[];
  followupPlan: string;
  customer: Customer | null;
  summary: string;
}

interface FormErrors {
  objective?: string;
  dateOfVisit?: string;
  content?: string;
  products?: string;
  followupPlan?: string;
  customer?: string;
  summary?: string;
}

interface ReportModalProps {
  onClose: () => void;
  onSubmit: (formData: ApiSubmissionData) => void;
  isLoading: boolean;
}

const ReportModal: React.FC<ReportModalProps> = ({
  onClose,
  onSubmit,
  isLoading,
}) => {
  const { dateOfVisit, setDateOfVisit, customerDetails, setCustomerDetails } =
    useDateOfVisit() ?? {};

  const [formData, setFormData] = useState<FormData>({
    objective: null,
    dateOfVisit: "",
    content: [],
    products: [],
    followupPlan: "",
    customer: null,
    summary: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    if (formData.customer) {
      setCustomerDetails?.(formData.customer);
    }
  }, [formData.customer, setCustomerDetails]);

  useEffect(() => {
    const isValid =
      Boolean(formData.objective) &&
      Boolean(dateOfVisit) &&
      formData.content.length > 0 &&
      formData.products.length > 0 &&
      formData.customer !== null;

    setIsFormValid(isValid);
  }, [formData, dateOfVisit]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!formData.objective) {
      newErrors.objective = "Objective is required";
      isValid = false;
    }

    if (!dateOfVisit) {
      newErrors.dateOfVisit = "Date of visit is required";
      isValid = false;
    }

    if (!formData.content || formData.content.length === 0) {
      newErrors.content = "At least one content must be selected";
      isValid = false;
    }

    if (!formData.customer) {
      newErrors.customer = "Customer is required";
      isValid = false;
    }

    if (!formData.products || formData.products.length === 0) {
      newErrors.products = "At least one product must be selected";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "dateOfVisit") {
      setDateOfVisit?.(value);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      if (!formData.customer || !formData.objective) {
        return;
      }

      const modifiedFormData: ApiSubmissionData = {
        objective: formData.objective,
        dateOfVisit: dateOfVisit || "",
        content: formData.content,
        products: formData.products,
        followupPlan: formData.followupPlan || "",
        customer: formData.customer.customer,
        timeOfVisit: customerDetails?.time_of_visit,
        summary: formData.summary || "",
      };

      onSubmit(modifiedFormData);
      localStorage.removeItem("dateOfVisit");
      localStorage.removeItem("customerDetails");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-start justify-center pt-8 pb-8"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />
        <motion.div
          className="bg-white w-full max-w-lg mx-4 rounded-lg shadow-lg relative z-10 flex flex-col max-h-[90vh]"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <div className="flex-shrink-0 sticky top-0 bg-white p-4 border-b border-gray-200 rounded-t-lg">
            <button
              className="absolute right-4 top-4 text-gray-500 text-2xl text-red-500"
              onClick={onClose}
            >
              &times;
            </button>
            <h2 className="text-xl font-bold">Report</h2>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <div className="p-4 flex-1 overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700">Date of Visit</label>
                  <input
                    type="date"
                    className={`w-full p-2 border rounded-md ${
                      errors.dateOfVisit ? "border-red-500" : "border-gray-300"
                    }`}
                    name="dateOfVisit"
                    value={dateOfVisit || ""}
                    onChange={handleInputChange}
                  />
                  {errors.dateOfVisit && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.dateOfVisit}
                    </p>
                  )}
                </div>

                {dateOfVisit && (
                  <CustomerSelector
                    selectedCustomer={formData.customer}
                    onCustomerSelect={(customer) => {
                      setFormData((prev) => ({ ...prev, customer }));
                      setErrors((prev) => ({ ...prev, customer: undefined }));
                    }}
                    error={errors.customer}
                  />
                )}

                {customerDetails?.time_of_visit && (
                  <div>
                    <label className="block text-gray-700">Time of Visit</label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-md border-gray-300 bg-gray-100"
                      value={customerDetails.time_of_visit}
                      readOnly
                    />
                  </div>
                )}

                <ObjectiveInput
                  value={formData.objective || ""}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      objective: e.target.value,
                    }));
                    setErrors((prev) => ({ ...prev, objective: undefined }));
                  }}
                  error={errors.objective}
                />

                <ContentSelector
                  selectedContents={formData.content}
                  onContentsChange={(contents) => {
                    setFormData((prev) => ({ ...prev, content: contents }));
                    setErrors((prev) => ({ ...prev, content: undefined }));
                  }}
                  error={errors.content}
                />

                <ProductSelector
                  selectedProducts={formData.products}
                  onProductsChange={(products) => {
                    setFormData((prev) => ({ ...prev, products }));
                    setErrors((prev) => ({ ...prev, products: undefined }));
                  }}
                  error={errors.products}
                />

                <div>
                  <label className="block text-gray-700">
                    Discussion Summary
                  </label>
                  <textarea
                    className={`w-full p-2 border rounded-md ${
                      errors.summary ? "border-red-500" : "border-gray-300"
                    }`}
                    rows={4}
                    placeholder="Enter discussion summary"
                    name="summary"
                    value={formData.summary}
                    onChange={handleInputChange}
                  />
                  {errors.summary && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.summary}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700">Follow-up Plan</label>
                  <textarea
                    className={`w-full p-2 border rounded-md ${
                      errors.followupPlan ? "border-red-500" : "border-gray-300"
                    }`}
                    rows={4}
                    placeholder="Enter follow-up plan"
                    name="followupPlan"
                    value={formData.followupPlan}
                    onChange={handleInputChange}
                  />
                  {errors.followupPlan && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.followupPlan}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-shrink-0 sticky bottom-0 bg-white p-4 border-t border-gray-200 rounded-b-lg">
              <button
                type="submit"
                className={`w-full py-2 rounded-md flex items-center justify-center ${
                  isFormValid && !isLoading
                    ? "bg-blue-900 text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
                disabled={!isFormValid || isLoading}
              >
                {isLoading ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  "Create Report"
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ReportModal;
